/**
 * NEX-1645 (plan 2026-10-20, Tarea 3.3) — the storage module's state, as nexvia-api-web derives it from
 * its two feature flags (`storage_module_enabled`, `storage_client_requests_enabled`; Tarea 3.1) and
 * answers it on the public `GET /app/features` (contracts/app-features.ts):
 *
 *  - `off`: the module is closed. The client web shows nothing of it — no «Depósitos» in the menu, no
 *    /storage/* page, no link to the depósito panel — even to a client with a depósito in course
 *    (Johan's answer 8: the API keeps processing its payments, jobs and backoffice; only the screens hide).
 *  - `registration_only`: depósitos can register and get approved; a client still sees and manages
 *    what they already have («Depósitos», its list, quotes and management), but cannot ask for a new one.
 *  - `full`: everything.
 */
export type StorageModuleState = 'off' | 'registration_only' | 'full';

/** What the web assumes while the state loads or when it cannot be read: closed, the safe side (plan 3.3). */
export const STORAGE_MODULE_FALLBACK: StorageModuleState = 'off';

/**
 * A client can ask for a new depósito: the wizard's «Depósitos» category, the mudanza's «¿Una parte va a
 * un depósito?» toggle and the list's «Publicar una solicitud». Otherwise they read «Próximamente».
 */
export function canRequestStorage(state: StorageModuleState): boolean {
  return state === 'full';
}

/**
 * A client can start paying for a depósito: «Contratar» a quote (pago único or the monthly subscription,
 * also from the mudanza + depósito payment screen) and «Renovar» a one-time depósito. With
 * `registration_only` the API answers those calls (`POST /storage/payment/checkout`, `/subscribe`,
 * `POST /storage/requests/:id/renew`) with 503 STORAGE_REQUESTS_DISABLED, so the web does not offer them.
 * Everything else a client already has keeps working: a Mercado Pago link already opened, «Cancelar el
 * cobro mensual», the baja, the withdrawal and the reviews.
 */
export function canPayStorage(state: StorageModuleState): boolean {
  return state === 'full';
}

/**
 * The client sees the module at all: the «Depósitos» menu item and its badge, the /storage/* pages and
 * the link to the depósito panel. True for `registration_only` too, so nothing a client already has
 * (a request, a depósito in course) disappears while new requests are closed (Johan's answer 8).
 */
export function isStorageVisible(state: StorageModuleState): boolean {
  return state !== 'off';
}
